const key = "puppet-show-view";
const validId = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9_-]{1,200}$/.test(value);

export function readShowHandoffTicket(hash: string): string | null {
  const ticket = new URLSearchParams(hash.replace(/^#/, "")).get("ticket");
  return ticket && /^[A-Za-z0-9_-]{32,200}$/.test(ticket) ? ticket : null;
}

/** Navigation state may hold a nonce, but never the redeemable capability. */
export function createShowHandoffVault(
  options: {
    now?: () => number;
    nonce?: () => string;
  } = {}
) {
  const now = options.now ?? Date.now;
  const nonce =
    options.nonce ??
    (() =>
      Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join(""));
  const entries = new Map<
    string,
    { ticket: string | null; expiresAt: number }
  >();
  const prune = () => {
    for (const [id, value] of entries)
      if (value.expiresAt <= now()) entries.delete(id);
  };
  return {
    capture(hash: string): string | null {
      if (!new URLSearchParams(hash.replace(/^#/, "")).has("ticket"))
        return null;
      prune();
      const ticket = readShowHandoffTicket(hash);
      // Initial navigation and Capacitor's launch event may deliver the same
      // ticket. Share its nonce so StrictMode cannot restart a joined screen.
      if (ticket)
        for (const [id, value] of entries)
          if (value.ticket === ticket) return id;
      while (entries.size >= 16) entries.delete(entries.keys().next().value!);
      const id = nonce();
      entries.set(id, { ticket, expiresAt: now() + 90000 });
      return id;
    },
    read(id: string): string | null {
      prune();
      return entries.get(id)?.ticket ?? null;
    },
  };
}

const handoffs = createShowHandoffVault();
export const readCapturedShowHandoff = handoffs.read;

export function readShowHandoffNonce(state: unknown): string | null {
  const nonce = (state as { showHandoffNonce?: unknown } | null)
    ?.showHandoffNonce;
  return validId(nonce) ? nonce : null;
}

/** Call before native router navigation or any URL-observing helpers. */
export function prepareShowNavigation(
  location: Pick<Location, "hash" | "pathname" | "search">,
  vault = handoffs
): { path: string; state?: { showHandoffNonce: string } } {
  if (!/^\/show\/view\/?$/i.test(location.pathname))
    return { path: location.pathname + location.search + location.hash };
  const nonce = vault.capture(location.hash);
  return {
    path: location.pathname + location.search,
    ...(nonce ? { state: { showHandoffNonce: nonce } } : {}),
  };
}

/** The first entrypoint import runs this before config, router, and telemetry. */
export function captureInitialShowHandoff(
  location: Pick<Location, "hash" | "pathname" | "search">,
  history: Pick<History, "replaceState" | "state">,
  vault = handoffs
) {
  if (!/^\/show\/view\/?$/i.test(location.pathname)) return;
  const previous =
    history.state && typeof history.state === "object" ? history.state : {};
  const usr =
    previous.usr && typeof previous.usr === "object" ? previous.usr : {};
  if (!location.hash && !readShowHandoffNonce(usr)) return;
  const next = prepareShowNavigation(location, vault);
  const cleanUsr = { ...usr };
  // A reload has a new memory vault. Drop the old nonce so the receiver can
  // restore its nonsecret, identity-bound session hint instead.
  delete cleanUsr.showHandoffNonce;
  history.replaceState(
    { ...previous, usr: { ...cleanUsr, ...next.state } },
    "",
    next.path
  );
}

export function saveShowView(
  storage: () => Pick<Storage, "setItem">,
  view: { sessionId: string; uid: string }
) {
  try {
    storage().setItem(key, JSON.stringify(view));
  } catch {
    /* Storage is optional. */
  }
}

/** This hint grants nothing: Firestore checks the current guest's membership. */
export function readSavedShowView(
  storage: () => Pick<Storage, "getItem">,
  uid: string
): { sessionId: string; uid: string } | null {
  try {
    const value = JSON.parse(storage().getItem(key) ?? "null");
    return validId(value?.sessionId) && value.uid === uid
      ? { sessionId: value.sessionId, uid }
      : null;
  } catch {
    return null;
  }
}
